import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  TrendingUp, 
  FileText, 
  Receipt, 
  Handshake, 
  Megaphone,
  PenTool,
  Settings,
  LogOut,
  Home,
  Users,
  BarChart3,
  Upload,
  Plus,
  Search,
  Bell,
  ChevronRight,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Eye,
  Trash2,
  Send,
  Globe,
  Menu,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = 'USD') => {
  const symbols = { RUB: '₽', USD: '$', EUR: '€', CNY: '¥' };
  return `${symbols[currency] || currency}${new Intl.NumberFormat('en-US').format(value)}`;
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-GB');
};

const Sidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen }) => {
  const { t } = useLanguage();
  const { user, logout, isClient, isAgent, isPartner, isEmployee, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();

  const getMenuItems = () => {
    const common = [
      { id: 'overview', icon: Home, label: t('dash_overview') },
      { id: 'settings', icon: Settings, label: t('dash_settings') }
    ];

    if (isClient) {
      return [
        ...common.slice(0, 1),
        { id: 'documents', icon: FileText, label: t('dash_documents') },
        { id: 'invoices', icon: Receipt, label: t('dash_invoices') },
        { id: 'deals', icon: Handshake, label: t('dash_deals') },
        { id: 'offers', icon: Megaphone, label: t('dash_offers') },
        { id: 'blog', icon: PenTool, label: t('dash_blog') },
        ...common.slice(1)
      ];
    }

    if (isAgent) {
      return [
        ...common.slice(0, 1),
        { id: 'deals', icon: Handshake, label: t('dash_deals') },
        { id: 'clients', icon: Users, label: t('dashboard.clients') },
        ...common.slice(1)
      ];
    }

    if (isPartner) {
      return [
        ...common.slice(0, 1),
        { id: 'deals', icon: Handshake, label: t('dash_deals') },
        { id: 'portfolio', icon: BarChart3, label: t('dashboard.portfolio') },
        ...common.slice(1)
      ];
    }

    if (isEmployee) {
      const items = [
        ...common.slice(0, 1),
        { id: 'deals', icon: Handshake, label: t('dash_deals') }
      ];
      
      if (isManager) {
        items.push({ id: 'analytics', icon: BarChart3, label: t('dash_analytics') });
      }
      
      if (isAdmin) {
        items.push({ id: 'users', icon: Users, label: t('dash_users') });
      }
      
      return [...items, ...common.slice(1)];
    }

    return common;
  };

  const menuItems = getMenuItems();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 flex flex-col`}>
        <div className="p-6 border-b border-slate-800">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-lime-400 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-950" />
            </div>
            <span className="text-xl font-bold text-white font-serif">TradeFlow</span>
          </Link>
        </div>
        
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === item.id 
                    ? 'bg-lime-400/10 text-lime-400' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </ScrollArea>
        
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.full_name?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.company_name || user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={() => { logout(); navigate('/'); }}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('nav_logout')}
          </Button>
        </div>
      </aside>
    </>
  );
};

const TopBar = ({ setIsOpen }) => {
  const { t, language, changeLanguage, languages } = useLanguage();
  const { user } = useAuth();

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
      <div className="flex items-center space-x-4">
        <button 
          className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
          onClick={() => setIsOpen(true)}
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder={t('dashboard.search')} 
            className="pl-10 w-64 bg-slate-50 border-slate-200"
            data-testid="search-input"
          />
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <Select value={language} onValueChange={changeLanguage}>
          <SelectTrigger className="w-16 bg-transparent border-slate-200" data-testid="language-switcher">
            <Globe className="w-4 h-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((item) => (
              <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
        </Button>
      </div>
    </header>
  );
};

// Overview Tab
const OverviewTab = () => {
  const { t } = useLanguage();
  const { user, isClient, isPartner, isEmployee, isManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dealsRes = await axios.get(`${API}/deals`);
        setDeals(dealsRes.data.slice(0, 5));
        
        if (isManager) {
          const statsRes = await axios.get(`${API}/admin/stats`);
          setStats(statsRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, [isManager]);

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: { color: 'bg-amber-100 text-amber-700', label: t('dashboard.status.pending_approval') },
      approved: { color: 'bg-blue-100 text-blue-700', label: t('dashboard.status.approved') },
      funded: { color: 'bg-emerald-100 text-emerald-700', label: t('dashboard.status.funded') },
      rejected: { color: 'bg-rose-100 text-rose-700', label: t('dashboard.status.rejected') }
    };
    const style = styles[status] || { color: 'bg-slate-100 text-slate-700', label: status };
    return <Badge className={style.color}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t('dashboard.welcome')}, {user?.full_name?.split(' ')[0]}!</h1>
          <p className="text-slate-500">{t('dashboard.accountSummary')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isClient && (
          <>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('stats_exposure')}</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">{formatCurrency(user?.exposure_limit || 0)}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('dash_scoring')}</p>
                    <p className="text-2xl font-bold text-slate-900">{user?.scoring_status || t('dashboard.notCompleted')}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
        
        {(isManager || isPartner) && stats && (
          <>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('stats_total_financed')}</p>
                    <p className="text-2xl font-bold text-slate-900 font-mono">{formatCurrency(stats.total_financed)}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('stats_active_deals')}</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.active_deals}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Handshake className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('dashboard.users')}</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('stats_pending')}</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.pending_reviews}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Recent Deals */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-sans">{t('dashboard.recentDeals')}</CardTitle>
        </CardHeader>
        <CardContent>
          {deals.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t('dashboard.table.amount')}</TableHead>
                  <TableHead>{t('dashboard.table.advance')}</TableHead>
                  <TableHead>{t('dashboard.table.status')}</TableHead>
                  <TableHead>{t('dashboard.table.date')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((deal) => (
                  <TableRow key={deal.id}>
                    <TableCell className="font-mono text-sm">{deal.id.slice(0, 8)}...</TableCell>
                    <TableCell className="font-mono">{formatCurrency(deal.amount)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(deal.advance_amount)}</TableCell>
                    <TableCell>{getStatusBadge(deal.status)}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(deal.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-500">
              {t('dashboard.noDeals')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Documents Tab
const DocumentsTab = () => {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ document_type: 'invoice', title: '', description: '', file: null });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error(t('dashboard.documents.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newDoc.file) {
      toast.error(t('dashboard.documents.selectFile'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', newDoc.file);
    formData.append('document_type', newDoc.document_type);
    formData.append('title', newDoc.title);
    formData.append('description', newDoc.description || '');

    try {
      await axios.post(`${API}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('dashboard.documents.uploadSuccess'));
      setDialogOpen(false);
      setNewDoc({ document_type: 'invoice', title: '', description: '', file: null });
      fetchDocuments();
    } catch (error) {
      toast.error(t('dashboard.documents.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success(t('dashboard.documents.deleteSuccess'));
      fetchDocuments();
    } catch (error) {
      toast.error(t('dashboard.documents.deleteError'));
    }
  };

  const docTypes = [
    { value: 'invoice', label: t('dashboard.documents.invoice') },
    { value: 'financial_statement', label: t('dashboard.documents.financial_statement') },
    { value: 'bank_statement', label: t('dashboard.documents.bank_statement') },
    { value: 'incorporation', label: t('dashboard.documents.incorporation') },
    { value: 'contract', label: t('dashboard.documents.contract') },
    { value: 'other', label: t('dashboard.documents.other') }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending_review: { color: 'bg-amber-100 text-amber-700', label: t('dashboard.status.pending_review') },
      approved: { color: 'bg-emerald-100 text-emerald-700', label: t('dashboard.status.approved') },
      rejected: { color: 'bg-rose-100 text-rose-700', label: t('dashboard.status.rejected') }
    };
    const style = styles[status] || { color: 'bg-slate-100 text-slate-700', label: status };
    return <Badge className={style.color}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="documents-tab">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">{t('dash_documents')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-950 hover:bg-emerald-900" data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-2" />
              {t('action_upload')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('dashboard.documents.uploadTitle')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('dashboard.documents.type')}</Label>
                <Select value={newDoc.document_type} onValueChange={(v) => setNewDoc({...newDoc, document_type: v})}>
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {docTypes.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('dashboard.documents.title')}</Label>
                <Input 
                  value={newDoc.title} 
                  onChange={(e) => setNewDoc({...newDoc, title: e.target.value})}
                  required
                  data-testid="doc-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('dashboard.documents.description')}</Label>
                <Textarea 
                  value={newDoc.description} 
                  onChange={(e) => setNewDoc({...newDoc, description: e.target.value})}
                  data-testid="doc-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('dashboard.documents.file')}</Label>
                <Input 
                  type="file" 
                  onChange={(e) => setNewDoc({...newDoc, file: e.target.files[0]})}
                  required
                  data-testid="doc-file-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={uploading} data-testid="doc-submit-btn">
                {uploading ? t('msg_loading') : t('action_upload')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t('msg_loading')}</div>
          ) : documents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.table.title')}</TableHead>
                  <TableHead>{t('dashboard.table.type')}</TableHead>
                  <TableHead>{t('dashboard.table.size')}</TableHead>
                  <TableHead>{t('dashboard.table.status')}</TableHead>
                  <TableHead>{t('dashboard.table.date')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.title}</TableCell>
                    <TableCell>{docTypes.find(d => d.value === doc.document_type)?.label || doc.document_type}</TableCell>
                    <TableCell className="text-slate-500">{(doc.file_size / 1024).toFixed(1)} KB</TableCell>
                    <TableCell>{getStatusBadge(doc.status)}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(doc.uploaded_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{t('msg_no_data')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Invoices Tab
const InvoicesTab = () => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    invoice_number: '',
    debtor_name: '',
    debtor_country: 'Kazakhstan',
    amount: '',
    currency: 'USD',
    issue_date: '',
    due_date: '',
    description: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error(t('dashboard.invoices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, {
        ...newInvoice,
        amount: parseFloat(newInvoice.amount)
      });
      toast.success(t('dashboard.invoices.createSuccess'));
      setDialogOpen(false);
      setNewInvoice({
        invoice_number: '',
        debtor_name: '',
        debtor_country: 'Kazakhstan',
        amount: '',
        currency: 'USD',
        issue_date: '',
        due_date: '',
        description: ''
      });
      fetchInvoices();
    } catch (error) {
      toast.error(t('dashboard.invoices.createError'));
    }
  };

  const handleFactoring = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/factoring`, { invoice_id: invoiceId, requested_advance_percent: 80 });
      toast.success(t('dashboard.invoices.factoringSuccess'));
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dashboard.genericError'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { color: 'bg-emerald-100 text-emerald-700', label: t('dashboard.status.active') },
      pending: { color: 'bg-amber-100 text-amber-700', label: t('dashboard.status.pending_approval') },
      approved: { color: 'bg-blue-100 text-blue-700', label: t('dashboard.status.approved') },
      funded: { color: 'bg-purple-100 text-purple-700', label: t('dashboard.status.funded') }
    };
    const style = styles[status] || { color: 'bg-slate-100 text-slate-700', label: status };
    return <Badge className={style.color}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">{t('dash_invoices')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-950 hover:bg-emerald-900" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('action_create')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('dashboard.invoices.createTitle')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('dashboard.invoices.number')}</Label>
                  <Input 
                    value={newInvoice.invoice_number} 
                    onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                    required
                    data-testid="invoice-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.table.amount')}</Label>
                  <Input 
                    type="number"
                    value={newInvoice.amount} 
                    onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                    required
                    data-testid="invoice-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('dashboard.invoices.debtor')}</Label>
                <Input 
                  value={newInvoice.debtor_name} 
                  onChange={(e) => setNewInvoice({...newInvoice, debtor_name: e.target.value})}
                  required
                  data-testid="invoice-debtor-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('dashboard.invoices.issueDate')}</Label>
                  <Input 
                    type="date"
                    value={newInvoice.issue_date} 
                    onChange={(e) => setNewInvoice({...newInvoice, issue_date: e.target.value})}
                    required
                    data-testid="invoice-issue-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.invoices.dueDate')}</Label>
                  <Input 
                    type="date"
                    value={newInvoice.due_date} 
                    onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                    required
                    data-testid="invoice-due-date-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="invoice-submit-btn">
                {t('action_create')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t('msg_loading')}</div>
          ) : invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.table.number')}</TableHead>
                  <TableHead>{t('dashboard.table.debtor')}</TableHead>
                  <TableHead>{t('dashboard.table.amount')}</TableHead>
                  <TableHead>{t('dashboard.table.dueDate')}</TableHead>
                  <TableHead>{t('dashboard.table.status')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.debtor_name}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(inv.due_date)}</TableCell>
                    <TableCell>{getStatusBadge(inv.factoring_status || inv.status)}</TableCell>
                    <TableCell>
                      {!inv.factoring_status && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleFactoring(inv.id)}
                          data-testid={`factoring-btn-${inv.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {t('dashboard.invoices.factoring')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{t('msg_no_data')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Deals Tab
const DealsTab = () => {
  const { t } = useLanguage();
  const { isEmployee, isPartner, isRiskManager } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`);
      setDeals(response.data);
    } catch (error) {
      toast.error(t('dashboard.deals.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (dealId) => {
    try {
      await axios.put(`${API}/deals/${dealId}/approve`);
      toast.success(t('dashboard.deals.approveSuccess'));
      fetchDeals();
    } catch (error) {
      toast.error(t('dashboard.genericError'));
    }
  };

  const handleFund = async (dealId) => {
    try {
      await axios.put(`${API}/deals/${dealId}/fund`);
      toast.success(t('dashboard.deals.fundSuccess'));
      fetchDeals();
    } catch (error) {
      toast.error(t('dashboard.genericError'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: { color: 'bg-amber-100 text-amber-700', label: t('dashboard.status.pending_approval') },
      approved: { color: 'bg-blue-100 text-blue-700', label: t('dashboard.status.approved') },
      funded: { color: 'bg-emerald-100 text-emerald-700', label: t('dashboard.status.funded') },
      rejected: { color: 'bg-rose-100 text-rose-700', label: t('dashboard.status.rejected') }
    };
    const style = styles[status] || { color: 'bg-slate-100 text-slate-700', label: status };
    return <Badge className={style.color}>{style.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="deals-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_deals')}</h1>

      <Card className="bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t('msg_loading')}</div>
          ) : deals.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t('dashboard.table.amount')}</TableHead>
                  <TableHead>{t('dashboard.table.advance')}</TableHead>
                  <TableHead>{t('dashboard.table.fee')}</TableHead>
                  <TableHead>{t('dashboard.table.status')}</TableHead>
                  <TableHead>{t('dashboard.table.date')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((deal) => (
                  <TableRow key={deal.id}>
                    <TableCell className="font-mono text-sm">{deal.id.slice(0, 8)}...</TableCell>
                    <TableCell className="font-mono">{formatCurrency(deal.amount)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(deal.advance_amount)}</TableCell>
                    <TableCell>{deal.fee_percent}%</TableCell>
                    <TableCell>{getStatusBadge(deal.status)}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(deal.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {isRiskManager && deal.status === 'pending_approval' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleApprove(deal.id)}
                            data-testid={`approve-deal-${deal.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            {t('action_approve')}
                          </Button>
                        )}
                        {isPartner && deal.status === 'approved' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleFund(deal.id)}
                            data-testid={`fund-deal-${deal.id}`}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            {t('action_fund')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-8 text-center text-slate-500">
              <Handshake className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{t('msg_no_data')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Tab
const SettingsTab = () => {
  const { t, language, changeLanguage, languages } = useLanguage();
  const { user, updateLanguage } = useAuth();

  const handleLanguageChange = async (lang) => {
    changeLanguage(lang);
    try {
      await updateLanguage(lang);
      toast.success(t('dashboard.languageChanged'));
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_settings')}</h1>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-sans">{t('dashboard.profile')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">{t('dashboard.name')}</Label>
              <p className="font-medium">{user?.full_name}</p>
            </div>
            <div>
              <Label className="text-slate-500">{t('dashboard.company')}</Label>
              <p className="font-medium">{user?.company_name || '-'}</p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <Label className="text-slate-500">{t('dashboard.phone')}</Label>
              <p className="font-medium">{user?.phone || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-sans">{t('dashboard.interfaceLanguage')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-48" data-testid="settings-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((item) => (
                <SelectItem key={item.code} value={item.code}>{item.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-sans">{t('dashboard.socialAccounts')}</CardTitle>
          <CardDescription>{t('dashboard.linkedAccounts')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {['Google', 'Yandex', 'VK', 'WeChat'].map((provider) => (
            <div key={provider} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="font-medium">{provider}</span>
              <Badge variant="outline" className="text-slate-400">
                {t('auth_coming_soon')}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Analytics Tab (for managers)
const AnalyticsTab = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  if (!stats) {
    return <div className="p-8 text-center text-slate-500">{t('msg_loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_analytics')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">{t('stats_total_financed')}</p>
            <p className="text-3xl font-bold text-emerald-600 font-mono">{formatCurrency(stats.total_financed)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">{t('dashboard.monthlyRevenue')}</p>
            <p className="text-3xl font-bold text-blue-600 font-mono">{formatCurrency(stats.monthly_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">{t('dashboard.totalDeals')}</p>
            <p className="text-3xl font-bold text-slate-900">{stats.total_deals}</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">{t('dashboard.conversion')}</p>
            <p className="text-3xl font-bold text-purple-600">{stats.conversion_rate}%</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-sans">{t('dashboard.keyMetrics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-500">{t('dashboard.users')}</span>
                <span className="font-medium">{stats.total_users}</span>
              </div>
              <Progress value={Math.min(stats.total_users / 10, 100)} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-500">{t('dashboard.invoicesMetric')}</span>
                <span className="font-medium">{stats.total_invoices}</span>
              </div>
              <Progress value={Math.min(stats.total_invoices / 10, 100)} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-500">{t('stats_active_deals')}</span>
                <span className="font-medium">{stats.active_deals}</span>
              </div>
              <Progress value={Math.min(stats.active_deals / 10, 100)} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Users Tab (for admins)
const UsersTab = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error(t('dashboard.usersTab.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle`);
      toast.success(t('dashboard.usersTab.statusChanged'));
      fetchUsers();
    } catch (error) {
      toast.error(t('dashboard.genericError'));
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      client_buyer: 'bg-blue-100 text-blue-700',
      client_seller: 'bg-emerald-100 text-emerald-700',
      agent: 'bg-purple-100 text-purple-700',
      partner_investor: 'bg-amber-100 text-amber-700',
      partner_institution: 'bg-orange-100 text-orange-700',
      employee_risk: 'bg-rose-100 text-rose-700',
      employee_sales: 'bg-cyan-100 text-cyan-700',
      employee_manager: 'bg-indigo-100 text-indigo-700',
      employee_admin: 'bg-slate-800 text-white'
    };
    return <Badge className={colors[role] || 'bg-slate-100 text-slate-700'}>{role}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="users-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_users')}</h1>

      <Card className="bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t('msg_loading')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.name')}</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>{t('dashboard.company')}</TableHead>
                  <TableHead>{t('dashboard.table.role')}</TableHead>
                  <TableHead>{t('dashboard.table.status')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.full_name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.company_name || '-'}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell>
                      <Badge className={u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                        {u.is_active ? t('dashboard.activeUsers') : t('dashboard.blockedUsers')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleUser(u.id)}
                        data-testid={`toggle-user-${u.id}`}
                      >
                        {u.is_active ? t('dashboard.blockUser') : t('dashboard.unblockUser')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Placeholder tabs
const OffersTab = () => {
  const { t } = useLanguage();
  return (
    <div className="space-y-6" data-testid="offers-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_offers')}</h1>
      <Card className="bg-white">
        <CardContent className="p-8 text-center text-slate-500">
          <Megaphone className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>{t('dashboard.offersSoon')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

const BlogTab = () => {
  const { t } = useLanguage();
  return (
    <div className="space-y-6" data-testid="blog-tab">
      <h1 className="text-2xl font-semibold text-slate-900">{t('dash_blog')}</h1>
      <Card className="bg-white">
        <CardContent className="p-8 text-center text-slate-500">
          <PenTool className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>{t('dashboard.blogSoon')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">{t('msg_loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab />;
      case 'documents': return <DocumentsTab />;
      case 'invoices': return <InvoicesTab />;
      case 'deals': return <DealsTab />;
      case 'offers': return <OffersTab />;
      case 'blog': return <BlogTab />;
      case 'settings': return <SettingsTab />;
      case 'analytics': return <AnalyticsTab />;
      case 'users': return <UsersTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="dashboard">
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div className="flex-1 flex flex-col min-h-screen">
        <TopBar setIsOpen={setSidebarOpen} />
        <main className="flex-1 p-6 overflow-auto">
          {renderTab()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
